import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import FormattedTimeStat from 'src/features/pricingPlan/components/FormattedTimeStat';
import { useListPricingPlanCaps } from 'src/features/pricingPlan/api/caps/useListPricingPlanCaps';
import CreateCapFormModal from 'src/features/pricingPlan/components/form/caps/CreateCapFormModal';
import CellCapActions from 'src/features/pricingPlan/components/table/CellCapActions';

import { PricingPlanCap } from 'src/features/pricingPlan/utils/types';
import { tickTypeBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { CURRENCY } from 'src/types';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const PricingPlanCapsTable = ({ currency }: { currency?: CURRENCY }) => {
    const { formatMessage, formatNumber } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const managePricingPlanCaps = useHasPermissions([PERMISSION.MANAGE_PRICING_PLAN_CAPS]);

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'order', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: pricingPlanCapsData,
        isLoading,
        isError,
    } = useListPricingPlanCaps({
        planId,
        parkingId,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!planId,
        },
    });

    const columnHelper = createColumnHelper<PricingPlanCap>();

    const columns = [
        columnHelper.accessor('cap_minutes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row items-center gap-1">
                        <div>{row.original.cap_minutes}</div>
                        <div className="whitespace-nowrap">
                            (<FormattedTimeStat totalMinutes={row.original.cap_minutes} />)
                        </div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(commonMessages.minutesTitle),
            },
        }),
        columnHelper.accessor('cap_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: tickTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('rate_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.rate_with_tax;
                return value
                    ? formatNumber(value / 100, {
                          style: 'currency',
                          currency: currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(commonMessages.rate),
            },
        }),
        ...(managePricingPlanCaps
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellCapActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<PricingPlanCap, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={pricingPlanCapsData}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(pricingPlanMessages.caps),
                entryName: formatMessage(pricingPlanMessages.cap).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.caps).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlanCaps && { newEntry: CreateCapFormModal }),
            }}
        />
    );
};

export default PricingPlanCapsTable;
